import { useEffect, useState, useRef } from "react";
import { isAPIError } from "lib/api/base";
import {
  searchForSchool,
  getClassroom,
  GetSearchForSchoolResponse,
  School,
  ClassroomAffiliate,
} from "lib/api/affiliates";

export const AFFILIATE_SEARCH_SCHOOL = "AFFILIATE_SEARCH_SCHOOL";
export const AFFILIATE_SEARCH_TEACHER = "AFFILIATE_SEARCH_TEACHER";

export function useAffiliateSearch({
  query,
  type = AFFILIATE_SEARCH_SCHOOL,
}: {
  query: string;
  type?: string;
}) {
  const [results, setResults] = useState(
    [] as Array<School | ClassroomAffiliate>
  );
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [load, loadMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(false);

  const isCancelled = useRef(false);

  // Build the request and fetch the data
  const fetchData = async () => {
    setIsFetching(true);
    setError(false);

    const request =
      AFFILIATE_SEARCH_SCHOOL === type
        ? await searchForSchool(query, page, 100)
        : await getClassroom(query);
    let data;

    if (!isCancelled.current) {
      if (isAPIError(request) || !request) {
        setError(true);
      } else {
        // For searches by school
        if (AFFILIATE_SEARCH_SCHOOL === type) {
          const response = request as GetSearchForSchoolResponse;
          data = response.results as Array<School> | [];

          // If nothing returned and we don't already have results then show an error
          if (!response.count && !results.length) {
            setError(true);
          }

          // If the count is more than we currently have, then set has more
          if (results.length + data.length < response.count) {
            setHasMore(true);
          }

          // For searches by teacher
        } else {
          const response = request as ClassroomAffiliate;
          data = [response] as Array<ClassroomAffiliate> | [];
        }

        // If we have more than 1 page then merge them
        if (page > 1) {
          data = results.concat(data);
        }

        setResults(data);
      }

      setIsFetching(false);
    }
  };

  // This is used to cancel any open requests
  useEffect(() => {
    isCancelled.current = false;

    return () => {
      isCancelled.current = true;
    };
  }, []);

  // Load more results
  useEffect(() => {
    if (load) {
      // Can only load more on schools
      if (AFFILIATE_SEARCH_SCHOOL === type && hasMore) {
        setPage(page + 1);
      }

      loadMore(false);
    }
  }, [load, page, hasMore, setHasMore]);

  useEffect(() => {
    if (page > 1 && !isFetching) {
      fetchData();
    }
  }, [page]);

  useEffect(() => {
    setError(false);
    setHasMore(false);
  }, [type]);

  // This is the initial call when a query Changes
  useEffect(() => {
    setError(false);
    setHasMore(false);
    setPage(1);

    // Only search if we have more than 1 character
    if (query?.length > 1 && !isFetching) {
      fetchData();
    } else {
      setResults([]);
    }
  }, [query]);

  return {
    error,
    isFetching,
    hasMore,
    loadMore,
    results,
  };
}
